import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import {
  getCoachesThatLikeAthlete,
  likeAthlete,
  adminLikeAthlete,
} from "../../services/AthleteService";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import AthleteSearchEvent from "./athlete-search-event";
import LoadingSpinner from "../LoadingSpinner";
import CircularProgressBar from "../CircularProgressBar";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../store/slices/accountSlice";
import { useDispatch } from "react-redux";
import CopyUrl from "./share-profile/copy-url";
import ShareProfileCardButton from "./share-profile/share-profile-card-button";
import AthleteCompareModal from "../../components/AthleteCompareModal";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import {
  toggleFavouriteLocal,
  adjustAthleteLikes,
} from "../../store/slices/athleteSlice";
import AthleteMeasurementVideoViewer from "./measurements/athlete_measurement_videoviewer";

const INTRO_VIDEO_LABEL = "Athlete Summary Intro Video";

function AthleteInformation({
  sharedProfile = false,
  profile = {},
  userId,
  onFavoritesRefresh,
}) {
  const apiInstance = useAxiosInstance();
  const r_athlete = useSelector((state) => state.athletes);
  const likedOverrides = useSelector((state) => state.athletes.likedOverrides);
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  let [isOpen, setIsOpen] = useState(false);
  const [coaches, setCoaches] = useState([]);
  // Breakdown returned alongside `coaches` by the extended coach-likes route —
  // admin likes are an anonymous counter, so an admin who likes never shows
  // up as an entry in `coaches`.
  const [coachLikeCount, setCoachLikeCount] = useState(0);
  const [adminLikeCount, setAdminLikeCount] = useState(0);
  const authUser = useAuthUser();
  const user = authUser?.user ?? {};
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const isLocked = sharedProfile && !hasSubscription;
  const [showMore, setShowMore] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [adminLikeLoading, setAdminLikeLoading] = useState(false);
  const [adminUnlikeLoading, setAdminUnlikeLoading] = useState(false);
  const [justLiked, setJustLiked] = useState(false);
  // Guards against double-submits from clicks in the same tick: state
  // updates (setAdminLikeLoading) are batched/async, so a ref is the only
  // thing guaranteed to be current before the next click is handled.
  const adminLikeInFlightRef = useRef(false);

  const resolvedId = userId ?? profile?.id;

  // The player-card routes act on the signed-in athlete, so the link only
  // shows on their own profile — not on another athlete's opened from the
  // leaderboard or Compare. This repo has no subscriptions, so every
  // athlete gets the link regardless of hasSubscription.
  const isOwnProfile =
    user.id != null && String(user.id) === String(resolvedId);
  const canShareProfileCard =
    user.role === "athlete" && isOwnProfile && !sharedProfile;

  // AthleteScore can pass either the Redux `state.athletes.profile` or a
  // profile it fetched locally (isolateProfile mode) — the latter never
  // reacts to Redux dispatches. Rather than copying profile.likes into
  // local state (which a re-render mid-flight would stomp back to the
  // pre-like value), we layer an override on top of it — the same pattern
  // `likedOverrides` uses for the coach toggle — and only clear it when
  // navigating to a different athlete.
  const [likesOverride, setLikesOverride] = useState(null);
  useEffect(() => {
    setLikesOverride(null);
  }, [resolvedId]);
  const likesCount = likesOverride ?? profile?.likes ?? 0;

  const isFavorite = (() => {
    if (!resolvedId) return false;
    if (resolvedId in likedOverrides) return likedOverrides[resolvedId];
    const foundInFavourites = r_athlete?.favouriteAthletes?.find(
      (a) => a.userId === resolvedId,
    );
    if (foundInFavourites !== undefined) return foundInFavourites.liked ?? true;
    return profile?.liked ?? false;
  })();

  const loadCoaches = async () => {
    try {
      setLoading(true);
      const data = await getCoachesThatLikeAthlete(apiInstance, profile.id);
      setCoaches(data?.coaches ?? []);
      setCoachLikeCount(data?.coachLikeCount ?? 0);
      setAdminLikeCount(data?.adminLikeCount ?? 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (user.role === "admin") {
      loadCoaches();
      setIsOpen(true);
    }
  };

  const openSubscription = () => {
    const updatedData = {
      userId: user.id,
      role: "athlete",
      email: user.email,
      registered: true,
    };
    dispatch(clearRegisterData());
    dispatch(registerAccountStore(updatedData));
    localStorage.setItem("registeredAccount", JSON.stringify(updatedData));
    window.location.href = "/register/subscription";
  };
  const handleStarClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!resolvedId || likeLoading) return;
    const nextValue = !isFavorite;
    dispatch(
      toggleFavouriteLocal({ athleteUserId: resolvedId, liked: nextValue }),
    );

    try {
      setLikeLoading(true);
      await likeAthlete(apiInstance, nextValue, resolvedId);
      await onFavoritesRefresh?.();
    } catch (err) {
      dispatch(
        toggleFavouriteLocal({ athleteUserId: resolvedId, liked: !nextValue }),
      );
      toast.error("Error updating like status", { position: "top-center" });
    } finally {
      setLikeLoading(false);
    }
  };

  const getCircleBackgroundColor = () => {
    return isFavorite ? "bg-yellow-500" : "bg-gray-400";
  };

  // The admin-like endpoint returns the new combined (coach + admin) total
  // as a string (e.g. `"7"`, from the API's OKResultVM), not a number —
  // parse defensively instead of trusting the wire type. Reject anything
  // that isn't already a number or a non-empty string BEFORE coercing:
  // Number(null)/Number("")/Number(false)/Number([]) all coerce to 0, and
  // Number.isFinite(0) is true, so a missing/empty response body would
  // otherwise read as a legitimate total of 0 and wipe out the like count.
  const parseLikeTotal = (raw) => {
    const isNumeric =
      typeof raw === "number" || (typeof raw === "string" && raw.trim() !== "");
    if (!isNumeric) return null;
    const total = Number(raw);
    return Number.isFinite(total) ? total : null;
  };

  const handleAdminLikeClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    // Guard synchronously with the ref, not the `adminLikeLoading` state —
    // two clicks fired in the same tick would both read the pre-update
    // state value and slip past a state-only check. The ref is shared with
    // the decrement handler so an add and a remove can never race each other.
    if (!resolvedId || adminLikeInFlightRef.current) return;
    adminLikeInFlightRef.current = true;
    setAdminLikeLoading(true);

    const previousDisplayed = likesCount;
    setLikesOverride(previousDisplayed + 1);
    dispatch(adjustAthleteLikes({ athleteUserId: resolvedId, delta: 1 }));
    setJustLiked(true);
    setTimeout(() => setJustLiked(false), 600);

    try {
      const totalLikes = await adminLikeAthlete(apiInstance, true, resolvedId);
      const parsedTotal = parseLikeTotal(totalLikes);
      if (parsedTotal !== null) {
        setLikesOverride(parsedTotal);
        dispatch(
          adjustAthleteLikes({ athleteUserId: resolvedId, total: parsedTotal }),
        );
      }
      toast.success("Like added", { position: "top-center" });
    } catch (err) {
      setLikesOverride(previousDisplayed);
      dispatch(adjustAthleteLikes({ athleteUserId: resolvedId, delta: -1 }));
      toast.error("Error adding like", { position: "top-center" });
    } finally {
      setAdminLikeLoading(false);
      adminLikeInFlightRef.current = false;
    }
  };

  const handleAdminUnlikeClick = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    // Same shared in-flight ref as handleAdminLikeClick — mutually excludes
    // add/remove clicks — plus a state guard so the control can't fire once
    // the admin-like count has already reached zero (it should be hidden by
    // then, but this protects against a stray click during the fade-out).
    if (!resolvedId || adminLikeInFlightRef.current || adminLikeCount < 1) {
      return;
    }
    adminLikeInFlightRef.current = true;
    setAdminUnlikeLoading(true);

    const previousDisplayed = likesCount;
    const previousAdminLikeCount = adminLikeCount;
    setLikesOverride(Math.max(0, previousDisplayed - 1));
    setAdminLikeCount(Math.max(0, previousAdminLikeCount - 1));
    dispatch(adjustAthleteLikes({ athleteUserId: resolvedId, delta: -1 }));

    try {
      const totalLikes = await adminLikeAthlete(apiInstance, false, resolvedId);
      const parsedTotal = parseLikeTotal(totalLikes);
      if (parsedTotal !== null) {
        setLikesOverride(parsedTotal);
        dispatch(
          adjustAthleteLikes({ athleteUserId: resolvedId, total: parsedTotal }),
        );
      }
      toast.success("Like removed", { position: "top-center" });
    } catch (err) {
      setLikesOverride(previousDisplayed);
      setAdminLikeCount(previousAdminLikeCount);
      dispatch(adjustAthleteLikes({ athleteUserId: resolvedId, delta: 1 }));
      toast.error("Error removing like", { position: "top-center" });
    } finally {
      setAdminUnlikeLoading(false);
      adminLikeInFlightRef.current = false;
    }
  };

  const renderLikeIcon = () =>
    user.role === "admin" ? (
      <button
        type="button"
        onClick={handleAdminLikeClick}
        disabled={adminLikeLoading}
        aria-label="Add like"
        className="grow-0 shrink-0 disabled:opacity-60"
      >
        <img
          alt="Athlete Rating"
          src="/like.svg"
          className={`w-10 h-10 transition-transform duration-300 ${
            justLiked ? "scale-125" : ""
          }`}
        />
      </button>
    ) : (
      <img
        alt="Athlete Rating"
        src="/like.svg"
        className="w-10 h-10 grow-0 shrink-0"
      />
    );

  return (
    <>
      <div className="flex-grow pb-3 xl:pb-0 lg:pb-0">
        <div className="flex flex-wrap lg:justify-between bg-gray-100 lg:bg-transparent rounded-md gap-6 p-0 pb-2 mb-0 pr-5">
          <div className="w-full sm:w-2/5 md:w-3/4 xl:w-3/5">
            {isLocked ? (
              <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <img
                  src="/lock-svgrepo-com.svg"
                  alt="Locked"
                  className="w-5 h-5"
                />
                <p className="text-sm text-gray-700">
                  Subscribe to view this profile
                </p>
              </div>
            ) : (
              r_athlete?.config?.showProfile && (
                <>
                  <div className="flex items-center justify-start gap-4">
                    <img
                      alt={profile.firstName}
                      src={profile.profilePicture || "/default-profile-img.png"}
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">
                          <span className="text-lg font-medium uppercase text-gray-900 flex items-center gap-2">
                            {profile.firstName} {profile.lastName}
                            <CopyUrl athleteProfileId={profile.id} />
                          </span>
                        </h2>
                      </div>
                      <p className="text-gray-500 text-sm">
                        {profile.primaryPosition}
                      </p>
                      <p className="text-gray-500 text-sm">
                        #{profile.positionRank} (Overall Position Rank)
                      </p>
                      {/* School: mobile hidden by default, visible when expanded; desktop always visible */}
                      <p
                        className={`text-gray-500 text-sm ${showMore ? "" : "hidden xl:block"}`}
                      >
                        {profile.collegeSchool?.trim()
                          ? profile.collegeSchool
                          : profile.highSchool}
                        <span className="xl:hidden">
                          {profile.state ? `, ${profile.state}` : ""}
                          {profile.graduation ? ` (${profile.graduation})` : ""}
                        </span>
                      </p>
                      {/* Weight/Height: same visibility rules as school */}
                      <p
                        className={`text-sm pt-1 ${showMore ? "" : "hidden xl:block"}`}
                      >
                        <span className="font-normal capitalize">
                          Weight: <span>{profile.weight}</span>
                        </span>
                        <span className="font-normal capitalize pl-4">
                          Height:{" "}
                          <span>
                            {profile.heightFt}' {profile.heightIn}"
                          </span>
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Mobile-only: expanded stats row + toggle */}
                  <div className="xl:hidden">
                    {showMore && hasSubscription && (
                      <div className="flex items-center justify-around mt-3 pt-3 border-t border-gray-200">
                        {/* Likes */}
                        {r_athlete?.config?.showLikes && !sharedProfile && (
                          <div className="relative flex items-center">
                            {user.role === "coach" ||
                            user.role === "university" ? (
                              <button
                                className="text-[#8E8E93]"
                                onClick={handleStarClick}
                                type="button"
                              >
                                <div
                                  className={`flex items-center justify-center h-[32px] w-[32px] rounded-full ${getCircleBackgroundColor()}`}
                                >
                                  {likeLoading ? (
                                    <LoadingSpinner size="sm" />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={faThumbsUp}
                                      style={{ color: "#fff" }}
                                    />
                                  )}
                                </div>
                              </button>
                            ) : (
                              <div className="relative flex">
                                <div
                                  className={`absolute font-normal text-sm -top-2 left-7 text-red-500 bg-white border border-gray-300 rounded-full px-1.5${user.role === "admin" ? " cursor-pointer" : ""}`}
                                  onClick={
                                    user.role === "admin"
                                      ? handleOpenDialog
                                      : undefined
                                  }
                                >
                                  {likesCount}
                                </div>
                                {renderLikeIcon()}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Trackit Score */}
                        {r_athlete.config.showTrackittScore && (
                          <div className="flex flex-col items-center">
                            <CircularProgressBar
                              progress={profile.trackittScore || 0}
                              size={80}
                              strokeWidth={5}
                            />
                            <span className="text-[9px] text-gray-900 -mt-8">
                              Trackit Score
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* View more / View less toggle */}
                    <button
                      type="button"
                      onClick={() => setShowMore(!showMore)}
                      className="text-blue-600 text-sm mt-2 flex items-center gap-1 hover:text-blue-800"
                    >
                      {showMore ? (
                        <>
                          View less{" "}
                          <FontAwesomeIcon
                            icon={faChevronUp}
                            className="text-xs"
                          />
                        </>
                      ) : (
                        <>
                          View more{" "}
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="text-xs"
                          />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )
            )}
          </div>

          {!hasSubscription && !sharedProfile && (
            <div>
              <p className="text-sm font-light text-center pb-1 mt-4">
                Unlock premium features and exclusive content with a full
                subscription.
              </p>
              <button
                className="bg-red-500 text-white px-4 py-2 rounded-md uppercase text-sm hover:bg-red-700"
                onClick={openSubscription}
              >
                Subscribe Now
              </button>
            </div>
          )}

          {/* Desktop-only: Likes + position score section */}
          {hasSubscription && (
            <div className="hidden xl:flex flex-row justify-center items-center gap-6 w-full sm:pt-0 xl:pt-4 sm:justify-start sm:items-start sm:w-auto">
              {r_athlete?.config?.showLikes && !sharedProfile && (
                <div className="relative top-1 flex">
                  {user.role === "coach" || user.role === "university" ? (
                    <button
                      className="mr-2 xl:mr-6 text-[#8E8E93]"
                      onClick={handleStarClick}
                      type="button"
                    >
                      <div
                        className={`flex items-center justify-center
                      h-[32px] xl:h-[40px]
                      w-[32px] xl:w-[40px]
                      rounded-full ${getCircleBackgroundColor()}`}
                      >
                        {likeLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <FontAwesomeIcon
                            icon={faThumbsUp}
                            style={{ color: "#fff" }}
                          />
                        )}
                      </div>
                    </button>
                  ) : (
                    <div className="relative top-1 flex">
                      <div
                        className={`absolute font-normal text-sm -top-2 left-7 text-red-500 bg-white border border-gray-300 rounded-full px-1.5${user.role === "admin" ? " cursor-pointer" : ""}`}
                        onClick={
                          user.role === "admin" ? handleOpenDialog : undefined
                        }
                      >
                        {likesCount}
                      </div>
                      {renderLikeIcon()}
                    </div>
                  )}

                  <div className="flex flex-col justify-start items-center ml-9">
                    <div>
                      {r_athlete.config.searchEvent && <AthleteSearchEvent />}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {!isLocked &&
          (hasSubscription ||
            profile.profileSummaryVideoLink ||
            canShareProfileCard) && (
            <div className="flex-grow items-center justify-between flex py-3 xl:py-0 lg:py-0">
              <div>
                {profile.profileSummaryVideoLink && (
                  <AthleteMeasurementVideoViewer
                    title={
                      <>
                        {`${profile.firstName} ${profile.lastName}`}
                        <span className="font-normal pl-3 text-base">
                          {INTRO_VIDEO_LABEL}
                        </span>
                      </>
                    }
                    linkText={INTRO_VIDEO_LABEL}
                    videoUrls={[profile.profileSummaryVideoLink]}
                  />
                )}
              </div>
              {(hasSubscription || canShareProfileCard) && (
                <div className="flex items-center gap-4">
                  {hasSubscription && (
                    <AthleteCompareModal
                      athlete={r_athlete}
                      onClose={() => setIsOpen(false)}
                    />
                  )}
                  {canShareProfileCard && <ShareProfileCardButton />}
                </div>
              )}
            </div>
          )}

        {/* Dialogue to see coaches that the athlete likes */}
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="relative z-[10000]"
        >
          <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
          <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
            <DialogPanel className="w-full max-w-2xl max-h-full space-y-4 border bg-white rounded-lg p-6">
              <DialogTitle className="text-xl uppercase pb-0 font-bold">
                <div className="flex justify-between items-center mb-4">
                  <h2>Coaches who liked your profile</h2>
                  <button
                    className="text-gray-500 hover:text-gray-700 text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    ✕
                  </button>
                </div>
              </DialogTitle>

              {(user.role === "athlete" || user.role === "admin") && (
                <div className="max-h-screen overflow-x-hidden overflow-y-auto pr-3">
                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {/* Like breakdown + admin-like decrement control are
                          admin-only — athletes/coaches never see the count
                          split or get a way to remove an admin like. */}
                      {user.role === "admin" && (
                        <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b border-gray-200">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold text-gray-900">
                              {coachLikeCount + adminLikeCount} total
                            </span>{" "}
                            — {coachLikeCount} coaches, {adminLikeCount} staff
                          </p>
                          {adminLikeCount >= 1 && (
                            <button
                              type="button"
                              onClick={handleAdminUnlikeClick}
                              disabled={adminUnlikeLoading}
                              className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0 ml-3"
                            >
                              {adminUnlikeLoading
                                ? "Removing…"
                                : "Remove staff like"}
                            </button>
                          )}
                        </div>
                      )}
                      <ul className="list-none p-0 mt-0 overflow-y-auto pr-3 -mr-4">
                        {coaches.map((coach, key) => (
                          <li key={key} className="p-2 border-b px-2 shrink-0">
                            <div className="flex justify-between items-center">
                              <div className="flex flex-row flex-1">
                                <div className="my-auto rounded-md">
                                  <img
                                    alt={coach.name}
                                    src={
                                      coach.profilePicture ||
                                      "/default-coach-img.png"
                                    }
                                    className="grow-0 shrink-0 rounded-md h-[40px] w-[40px]"
                                  />
                                </div>
                                <div className="ml-4">
                                  <p className="text-md uppercase">
                                    {coach.name}
                                  </p>
                                  <p className="text-sm text-gray-700">
                                    {coach.university}
                                  </p>
                                </div>
                              </div>
                              <div>
                                <p className="my-auto mt-2 text-sm text-gray-500">
                                  {coach.likesDate}
                                </p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </DialogPanel>
          </div>
        </Dialog>
      </div>
    </>
  );
}
export default AthleteInformation;
